import React, { useCallback, useMemo, useState } from "react";
import { LocationsFilter } from "../Locations/LocationsFilter";
import { LocationsPayload, LocationType } from "../Locations/types";
import { LOCATION_TYPES } from "../Locations/constants";
import { useFiltersContext } from "../../../pages/Workspace/UserFilters/FiltersContext";
import { CompanyFilter } from "../../../legacy/types/companyFilter";
import { useInsideFilterChip } from "../../../pages/Workspace/UserFilters/FiltersChips/hooks/useInsideFilterChip";
import { FilterLayout } from "./FilterLayout";

export const GeographyFilter: React.FC = () => {
  const { draftFilters, setDraftFilters } = useFiltersContext();
  const { insideChip, elementRef } = useInsideFilterChip();

  // Determine initial location type based on existing data
  const getInitialLocationType = (): LocationType => {
    if (draftFilters?.stateIds && draftFilters.stateIds.length > 0) {
      return LOCATION_TYPES.STATE;
    }
    if (draftFilters?.regionNames && draftFilters.regionNames.length > 0) {
      return LOCATION_TYPES.REGION;
    }
    return LOCATION_TYPES.COUNTRY;
  };

  const [locationType, setLocationType] = useState<LocationType>(
    getInitialLocationType
  );

  const geographyPayload: LocationsPayload = useMemo(() => {
    return {
      countryAlpha2s: draftFilters?.countryAlpha2s || [],
      regionIds: draftFilters?.regionNames || [],
      stateIds: draftFilters?.stateIds || [],
      excludedCountryAlpha2s: draftFilters?.excludedCountryAlpha2s || [],
      selectedLocationType: locationType,
    };
  }, [
    draftFilters?.countryAlpha2s,
    draftFilters?.regionNames,
    draftFilters?.stateIds,
    draftFilters?.excludedCountryAlpha2s,
    locationType,
  ]);

  const handleGeographyChange = useCallback(
    (payload: LocationsPayload) => {
      // Update the location type state
      setLocationType(payload.selectedLocationType);

      const result: Partial<CompanyFilter> = {};

      if (payload.countryAlpha2s !== undefined) {
        result.countryAlpha2s =
          payload.countryAlpha2s.length > 0
            ? payload.countryAlpha2s
            : undefined;
      }

      if (payload.regionIds !== undefined) {
        result.regionNames =
          payload.regionIds.length > 0 ? payload.regionIds : undefined;
      }

      if (payload.stateIds !== undefined) {
        result.stateIds =
          payload.stateIds.length > 0 ? payload.stateIds : undefined;
      }

      if (payload.excludedCountryAlpha2s !== undefined) {
        result.excludedCountryAlpha2s =
          payload.excludedCountryAlpha2s.length > 0
            ? payload.excludedCountryAlpha2s
            : undefined;
      }

      setDraftFilters((prevDraftFilters) => ({
        ...prevDraftFilters,
        ...result,
      }));
    },
    [setDraftFilters]
  );

  const handleClearFilter = useCallback(() => {
    setLocationType(LOCATION_TYPES.COUNTRY); // Reset to default type
    setDraftFilters((prevDraftFilters) => ({
      ...prevDraftFilters,
      countryAlpha2s: undefined,
      regionNames: undefined,
      stateIds: undefined,
      excludedCountryAlpha2s: undefined,
    }));
  }, [setDraftFilters]);

  return (
    <FilterLayout
      title="Geography"
      onClear={handleClearFilter}
      showHeader={!insideChip}
      refProp={elementRef}
      id="geography-filter"
    >
      <LocationsFilter
        selectedValues={geographyPayload}
        onValuesChange={handleGeographyChange}
        showExcluded
      />
    </FilterLayout>
  );
};
